import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Send, CheckCircle2, XCircle, ClipboardList, FileDown } from 'lucide-react';
import DataTable from './DataTable';
import { useAppContext } from '../context/AppContext';
import {
  formatDate,
  formatCurrency,
  getStatusText,
  getStatusTone,
  filesToAttachments,
  formatFileSize
} from '../utils/helpers';
import { generatePurchaseRequisitionPdf } from '../utils/pdf';
import EmptyState from './common/EmptyState';
import Badge from './common/Badge';
import Button from './common/Button';
import { t } from '../i18n';

const emptyLine = () => ({
  description: '',
  quantity: 1,
  unit: 'unidad',
  sku: '',
  unitPrice: 0
});

const PurchaseRequisitions = () => {
  const {
    currentUser,
    purchaseRequisitions,
    parks,
    suppliers,
    categories,
    setRequisitionBudgetOverrunAcceptance,
    createPurchaseRequisition,
    submitPurchaseRequisition,
    updateRequisitionApproval,
    assignRequisitionToBuyer,
    convertRequisitionToOrder,
    findAreaById,
    findParkById,
    findCostCenterById,
    findPackageById,
    findUserById,
    users
  } = useAppContext();

  if (!parks || parks.length === 0) {
    return (
      <EmptyState
        title={t('PR: no hay proyectos creados')}
        description={t(
          'Para crear una PR necesitas un proyecto con imputación mínima (área/CECO y WBS).'
        )}
        checklist={[
          'Crear proyecto en Desarrollo o Administración.',
          'Crear Área + CECO para el proyecto.',
          'Crear WBS/Paquetes.',
          'Crear categorías presupuestarias.'
        ]}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
        secondaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
      />
    );
  }

  const defaultPark = parks?.[0];
  const defaultArea = defaultPark?.areas?.[0];

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [note, setNote] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [createError, setCreateError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [routingCodeInput, setRoutingCodeInput] = useState('');
  const [assignedBuyerId, setAssignedBuyerId] = useState('');
  const [convertSupplierId, setConvertSupplierId] = useState('');
  const [convertCategoryId, setConvertCategoryId] = useState('');
  const [formState, setFormState] = useState({
    title: '',
    neededDate: '',
    parkId: defaultPark?.id || '',
    areaId: defaultArea?.id || '',
    costCenterId: defaultArea?.costCenterId || '',
    categoryId: '',
    packageId: '',
    type: 'material',
    priority: 'media',
    justification: '',
    attachments: [],
    lines: [emptyLine()]
  });

  const parkAreas = useMemo(() => {
    const park = formState.parkId ? findParkById(formState.parkId) : defaultPark;
    return park?.areas || [];
  }, [formState.parkId, findParkById, defaultPark]);

  const parkPackages = useMemo(() => {
    const park = formState.parkId ? findParkById(formState.parkId) : defaultPark;
    return park?.packages || [];
  }, [formState.parkId, findParkById, defaultPark]);

  useEffect(() => {
    if (!selectedRequisition) {
      setRoutingCodeInput('');
      setAssignedBuyerId('');
      setConvertSupplierId('');
      setConvertCategoryId('');
      return;
    }
    setRoutingCodeInput(selectedRequisition.routingCode || '');
    setAssignedBuyerId('');
    setConvertSupplierId('');
    setConvertCategoryId(selectedRequisition.categoryId || '');
  }, [selectedRequisition]);

  const currentCostCenter = formState.costCenterId ? findCostCenterById(formState.costCenterId) : null;

  const role = currentUser?.role || 'comprador';
  const isSuperUser = role === 'super_user';
  const isContractManager = role === 'contract_manager' || isSuperUser;
  const isPurchasingManager = role === 'purchasing_manager' || isSuperUser;
  const isRequester = [
    'admin_field',
    'admin_office',
    'office_staff',
    'area_manager',
    'project_manager',
    'comprador',
    'super_user'
  ].includes(role);
  const isAreaApprover = role === 'area_manager' || isSuperUser;
  const isController = role === 'purchase_controller' || isSuperUser;
  const isBuyer = role === 'comprador' || isSuperUser;

  const filteredRequisitions = useMemo(() => {
    if (isController || isContractManager || isPurchasingManager) return purchaseRequisitions;
    if (isBuyer) {
      return purchaseRequisitions.filter((req) => !req.routedToBuyerId || req.routedToBuyerId === currentUser.id);
    }
    return purchaseRequisitions.filter((req) => {
      if (req.requesterId === currentUser.id) return true;
      const area = req.areaId ? findAreaById(req.areaId) : null;
      if (area?.areaManagerId === currentUser.id) return true;
      const park = req.parkId ? findParkById(req.parkId) : null;
      if (park?.team?.projectManagerId === currentUser.id) return true;
      if (park?.team?.administrators?.includes(currentUser.id)) return true;
      return false;
    });
  }, [
    purchaseRequisitions,
    isController,
    isContractManager,
    isPurchasingManager,
    isBuyer,
    currentUser,
    findAreaById,
    findParkById
  ]);

  const getRequisitionWorkflowInfo = (requisition) => {
    if (!requisition) {
      return {
        stageKey: 'unknown',
        stageLabel: 'Sin estado',
        ownerName: '-',
        ownerId: null
      };
    }

    const approvals = requisition.approvals || {};

    if (requisition.status === 'borrador') {
      const requester = requisition.requesterId ? findUserById(requisition.requesterId) : null;
      return {
        stageKey: 'borrador',
        stageLabel: 'Pendiente envío del solicitante',
        ownerName: requester?.name || requisition.requesterName || 'Solicitante',
        ownerId: requester?.id || requisition.requesterId || null
      };
    }

    if (requisition.status === 'pendiente_area') {
      const ownerId = approvals.areaManager?.userId || null;
      const ownerName = ownerId ? findUserById(ownerId)?.name : 'Gerente de Área';
      return {
        stageKey: 'pendiente_area',
        stageLabel: 'Aprobación Gerente de Área',
        ownerName: ownerName || 'Gerente de Área',
        ownerId
      };
    }

    if (requisition.status === 'pendiente_controller') {
      const ownerId = approvals.purchaseController?.userId || null;
      const ownerName = ownerId ? findUserById(ownerId)?.name : 'Purchase Controller';
      return {
        stageKey: 'pendiente_controller',
        stageLabel: 'Revisión Purchase Controller',
        ownerName: ownerName || 'Purchase Controller',
        ownerId
      };
    }

    if (requisition.status === 'pendiente_compra') {
      if (requisition.routedToBuyerId) {
        const buyer = findUserById(requisition.routedToBuyerId);
        return {
          stageKey: 'pendiente_compra',
          stageLabel: 'Gestión del comprador asignado',
          ownerName: buyer?.name || 'Comprador asignado',
          ownerId: buyer?.id || requisition.routedToBuyerId
        };
      }
      const ownerId = approvals.purchaseController?.userId || null;
      const ownerName = ownerId ? findUserById(ownerId)?.name : 'Purchase Controller';
      return {
        stageKey: 'pendiente_compra',
        stageLabel: 'Pendiente asignación de comprador',
        ownerName: ownerName || 'Purchase Controller',
        ownerId
      };
    }

    if (requisition.status === 'convertida') {
      return {
        stageKey: 'convertida',
        stageLabel: `Convertida a OC ${requisition.linkedOrderId || ''}`.trim(),
        ownerName: requisition.linkedOrderId ? requisition.linkedOrderId : 'OC generada',
        ownerId: null
      };
    }

    if (requisition.status === 'rechazada') {
      return {
        stageKey: 'rechazada',
        stageLabel: 'Solicitud rechazada',
        ownerName: 'Revisar observaciones',
        ownerId: null
      };
    }

    return {
      stageKey: requisition.status || 'unknown',
      stageLabel: getStatusText(requisition.status) || 'Sin estado',
      ownerName: '-',
      ownerId: null
    };
  };

  const tableData = filteredRequisitions.map((req) => {
    const park = req.parkId ? findParkById(req.parkId) : null;
    const area = req.areaId ? findAreaById(req.areaId) : null;
    const costCenter = req.costCenterId ? findCostCenterById(req.costCenterId) : null;
    const workflow = getRequisitionWorkflowInfo(req);
    return {
      ...req,
      parkName: park?.name || '-',
      areaName: area?.name || '-',
      costCenterCode: costCenter?.code || '-',
      routingCode: req.routingCode || '-',
      requester: findUserById(req.requesterId)?.name || req.requesterName || '-',
      totalItems: req.lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0),
      workflowStage: workflow.stageLabel,
      workflowOwner: workflow.ownerName,
      workflowKey: workflow.stageKey,
      workflowOwnerId: workflow.ownerId
    };
  });

  const requisitionWorkflowCounters = useMemo(() => {
    const totals = {};
    const mine = {};
    filteredRequisitions.forEach((req) => {
      const workflow = getRequisitionWorkflowInfo(req);
      const key = workflow.stageKey || 'unknown';
      totals[key] = (totals[key] || 0) + 1;
      if (workflow.ownerId && workflow.ownerId === currentUser?.id) {
        mine[key] = (mine[key] || 0) + 1;
      }
    });
    return { totals, mine };
  }, [filteredRequisitions, currentUser?.id, findUserById]);

  const requisitionWorkflowCards = useMemo(() => {
    const meta = [
      {
        key: 'pendiente_area',
        label: t('Aprobación Gerente de Área'),
        tagline: 'Valida la necesidad y presupuesto del área'
      },
      {
        key: 'pendiente_controller',
        label: t('Revisión Purchase Controller'),
        tagline: 'Verificación de datos antes de compras'
      },
      {
        key: 'pendiente_compra',
        label: t('Gestión de Compras'),
        tagline: 'Asignación y generación de orden'
      }
    ];
    return meta
      .map((item) => ({
        ...item,
        count: requisitionWorkflowCounters.totals[item.key] || 0,
        mine: requisitionWorkflowCounters.mine[item.key] || 0
      }))
      .filter((card) => card.count > 0 || card.mine > 0);
  }, [requisitionWorkflowCounters]);

  const columns = useMemo(() => {
    const baseColumns = [
      { key: 'id', label: t('N° Solicitud'), sortable: true },
      { key: 'title', label: t('Título'), sortable: true },
      { key: 'requester', label: t('Solicitante'), sortable: true },
      {
        key: 'parkName',
        label: t('Parque'),
        sortable: true
      },
      {
        key: 'areaName',
        label: t('Área'),
        sortable: true
      },
      {
        key: 'costCenterCode',
        label: t('Centro Costo'),
        sortable: true
      },
      {
        key: 'neededDate',
        label: t('Fecha requerida'),
        sortable: true,
        render: (value) => formatDate(value)
      },
      {
        key: 'workflowStage',
        label: t('Siguiente paso'),
        render: (_, row) => (
          <div>
            <p className="text-sm font-semibold text-slate-800">{t(row.workflowStage) || '-'}</p>
            <p className="text-xs text-slate-500">{t(row.workflowOwner) || '—'}</p>
          </div>
        )
      },
      {
        key: 'status',
        label: t('Estado'),
        sortable: true,
        render: (status) => <Badge tone={getStatusTone(status)}>{t(getStatusText(status))}</Badge>
      },
      {
        key: 'totalItems',
        label: t('Total ítems'),
        sortable: true
      }
    ];

    if (isController || isSuperUser) {
      baseColumns.splice(6, 0, {
        key: 'routingCode',
        label: t('Código Parque'),
        sortable: true
      });
    }

    return baseColumns;
  }, [isController, isSuperUser]);

  const handleAttachmentUpload = async (event) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;
    setUploadError('');
    try {
      const uploads = await filesToAttachments(files);
      if (uploads.length === 0) return;
      setFormState((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...uploads]
      }));
    } catch (error) {
      console.error(error);
      setUploadError(t('No fue posible adjuntar algunos archivos. Inténtalo nuevamente.'));
    } finally {
      event.target.value = '';
    }
  };

  const removeAttachment = (attachmentId) => {
    setFormState((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((attachment) => attachment.id !== attachmentId)
    }));
  };

  const handleExportRequisition = () => {
    if (!selectedRequisition) return;
    const park = selectedRequisition.parkId ? findParkById(selectedRequisition.parkId) : null;
    const area = selectedRequisition.areaId ? findAreaById(selectedRequisition.areaId) : null;
    const costCenter = selectedRequisition.costCenterId
      ? findCostCenterById(selectedRequisition.costCenterId)
      : null;
    const requesterName = selectedRequisition.requesterId
      ? findUserById(selectedRequisition.requesterId)?.name
      : selectedRequisition.requesterName;

    generatePurchaseRequisitionPdf(selectedRequisition, {
      requester: requesterName || selectedRequisition.requesterName || 'Sin solicitante',
      parkName: park?.name,
      areaName: area?.name,
      costCenter: costCenter ? `${costCenter.code} · ${costCenter.name}` : '-'
    });
  };

  const handleCreate = (event) => {
    event.preventDefault();
    setCreateError('');
    try {
      const requisition = createPurchaseRequisition({
        ...formState,
        lines: formState.lines.map((line) => ({
          ...line,
          quantity: Number(line.quantity || 0),
          unitPrice: Number(line.unitPrice || 0)
        })),
        attachments: formState.attachments
      }, { submit: false });
      setShowCreateModal(false);
      setFormState({
        title: '',
        neededDate: '',
        parkId: defaultPark?.id || '',
        areaId: defaultArea?.id || '',
        costCenterId: defaultArea?.costCenterId || '',
        categoryId: '',
        packageId: '',
        type: 'material',
        priority: 'media',
        justification: '',
        attachments: [],
        lines: [emptyLine()]
      });
      setUploadError('');
      setSelectedRequisition(requisition);
    } catch (error) {
      setCreateError(error?.message || t('No fue posible crear la PR.'));
    }
  };

  const handleSubmit = (id) => {
    try {
      setSubmitError('');
      submitPurchaseRequisition(id);
    } catch (error) {
      setSubmitError(error?.message || t('No fue posible enviar la PR.'));
    }
  };

  const handleApproval = (req, stage, decision) => {
    updateRequisitionApproval(req.id, stage, decision, note.trim());
    setNote('');
  };

  const handleAssign = () => {
    if (!selectedRequisition || !assignedBuyerId) return;
    assignRequisitionToBuyer(
      selectedRequisition.id,
      assignedBuyerId,
      routingCodeInput.trim()
    );
    setAssignedBuyerId('');
  };

  const handleConvert = (req) => {
    if (!suppliers || suppliers.length === 0) {
      window.alert(t('Debes crear al menos 1 proveedor (y aprobarlo) antes de generar una OC.'));
      return;
    }
    if (!categories || categories.length === 0) {
      window.alert(t('Debes crear al menos 1 categoría antes de generar una OC.'));
      return;
    }
    if (!convertSupplierId) {
      window.alert(t('Selecciona un proveedor para generar la OC.'));
      return;
    }
    if (!convertCategoryId) {
      window.alert(t('Selecciona una categoría presupuestaria para la OC.'));
      return;
    }
    if (!req.packageId) {
      window.alert(t(
        'La requisición no tiene paquete/WBS. Edita y asigna un paquete antes de convertir.'
      ));
      return;
    }
    convertRequisitionToOrder(req.id, {
      supplierId: convertSupplierId,
      categoryId: convertCategoryId,
      items: req.lines.map((line) => ({
        sku: line.sku || null,
        description: line.description,
        unit: line.unit,
        quantity: line.quantity,
        unitPrice: line.unitPrice || 0
      })),
      totalValue: req.lines.reduce((sum, line) => sum + (Number(line.unitPrice || 0) * Number(line.quantity || 0)), 0)
    });
  };

  const estimateTotal = useMemo(() => {
    return (formState.lines || []).reduce(
      (sum, line) => sum + Number(line.quantity || 0) * Number(line.unitPrice || 0),
      0
    );
  }, [formState.lines]);

  const selectedPark = selectedRequisition?.parkId ? findParkById(selectedRequisition.parkId) : null;
  const selectedArea = selectedRequisition?.areaId ? findAreaById(selectedRequisition.areaId) : null;
  const selectedCostCenter = selectedRequisition?.costCenterId ? findCostCenterById(selectedRequisition.costCenterId) : null;
  const selectedPackage = selectedRequisition?.packageId && selectedRequisition?.parkId
    ? findPackageById(selectedRequisition.parkId, selectedRequisition.packageId)
    : null;

  const canCreateRequisitions =
    currentUser?.role === 'field_technician' ||
    currentUser?.role === 'office_staff' ||
    currentUser?.role === 'project_manager' ||
    currentUser?.role === 'super_user';

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Solicitudes de Compras')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Gestiona requisiciones de materiales y servicios antes de generar órdenes de compra.'
          )}</p>
        </div>
        {canCreateRequisitions && (
          <Button variant="primary" icon={Plus} onClick={() => setShowCreateModal(true)}>{t('Nueva requisición')}</Button>
        )}
      </div>

      {requisitionWorkflowCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {requisitionWorkflowCards.map((card) => (
            <div
              key={card.key}
              className="border border-slate-200 bg-white rounded-2xl px-4 py-3 shadow-sm"
            >
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {t(card.label)}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-2xl font-bold text-slate-900">{t(card.count)}</span>
                {card.mine > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-900 text-white">
                    {t(card.mine)} {t('a mi cargo')}</span>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">{t(card.tagline)}</p>
            </div>
          ))}
        </div>
      )}

      <DataTable
        data={tableData}
        columns={columns}
        searchPlaceholder={t('Buscar por solicitud, parque o estado...')}
        onRowClick={(row) => setSelectedRequisition(purchaseRequisitions.find((req) => req.id === row.id))}
      />

      {showCreateModal && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-6">
            <form
              className="bg-white rounded-3xl border border-slate-100 w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 space-y-6"
              onSubmit={handleCreate}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{t('Nueva requisición')}</h2>
                  <p className="text-sm text-slate-500">{t('Describe la necesidad de materiales o servicios.')}</p>
                </div>
                <button
                  type="button"
                  className="text-sm text-slate-500 hover:text-slate-900"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateError('');
                  }}
                >{t('cerrar')}</button>
              </div>

              {createError && (
                <div className="rounded-2xl border border-danger-100 bg-danger-50 p-4 text-sm text-danger-700">
                  {t(createError)}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Título')}</label>
                  <input
                    value={formState.title}
                    onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    placeholder={t('Ej: Reposición paneles zona B')}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Fecha requerida')}</label>
                  <input
                    type="date"
                    value={formState.neededDate}
                    onChange={(event) => setFormState((prev) => ({ ...prev, neededDate: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Parque')}</label>
                  <select
                    value={formState.parkId}
                    onChange={(event) => {
                      const nextParkId = event.target.value;
                      const park = findParkById(nextParkId) || parks.find((p) => p.id === nextParkId);
                      const firstArea = park?.areas?.[0];
                      const firstPackage = park?.packages?.[0];
                      setFormState((prev) => ({
                        ...prev,
                        parkId: nextParkId,
                        areaId: firstArea?.id || '',
                        costCenterId: firstArea?.costCenterId || '',
                        packageId: firstPackage?.id || ''
                      }));
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    required
                  >
                    {parks.map((park) => (
                      <option key={park.id} value={park.id}>
                        {t(park.name)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Área')}</label>
                  <select
                    value={formState.areaId}
                    onChange={(event) => {
                      const nextAreaId = event.target.value;
                      const area = findAreaById(nextAreaId);
                      setFormState((prev) => ({
                        ...prev,
                        areaId: nextAreaId,
                        costCenterId: area?.costCenterId || prev.costCenterId
                      }));
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    required
                  >
                    {parkAreas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {t(area.name)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Paquete / WBS')}</label>
                  <select
                    value={formState.packageId}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, packageId: event.target.value }))
                    }
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    required
                  >
                    <option value="">{t('Selecciona paquete…')}</option>
                    {parkPackages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.code ? `${pkg.code} · ` : ''}{t(pkg.name)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Centro de costo')}</label>
                  <input
                    type="text"
                    value={currentCostCenter ? `${currentCostCenter.code} · ${currentCostCenter.name}` : 'Sin asignar'}
                    readOnly
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Tipo')}</label>
                  <select
                    value={formState.type}
                    onChange={(event) => setFormState((prev) => ({ ...prev, type: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  >
                    <option value="material">{t('Material')}</option>
                    <option value="servicio">{t('Servicio')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Categoría presupuestaria')}</label>
                  <select
                    value={formState.categoryId}
                    onChange={(event) => setFormState((prev) => ({ ...prev, categoryId: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    required
                  >
                    <option value="">{t('Selecciona categoría…')}</option>
                    {(categories || []).map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {t(cat.name)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Prioridad')}</label>
                  <select
                    value={formState.priority}
                    onChange={(event) => setFormState((prev) => ({ ...prev, priority: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  >
                    <option value="baja">{t('Baja')}</option>
                    <option value="media">{t('Media')}</option>
                    <option value="alta">{t('Alta')}</option>
                  </select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Justificación')}</label>
                  <textarea
                    value={formState.justification}
                    onChange={(event) => setFormState((prev) => ({ ...prev, justification: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    rows={3}
                    placeholder={t('Describe la necesidad y el impacto en el proyecto')}
                  />
                </div>
                <div className="md:col-span-2 bg-slate-50 rounded-xl border border-slate-200 px-4 py-3">
                  <p className="text-xs text-slate-500 uppercase">{t('Estimado')}</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {formatCurrency(estimateTotal)}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">{t('Ítems solicitados')}</h4>
                  <button
                    type="button"
                    onClick={() => setFormState((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }))}
                    className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                  >{t('+ Añadir ítem')}</button>
                </div>

                <div className="space-y-3">
                  {formState.lines.map((line, index) => (
                    <div key={`line-${index}`} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-4 bg-slate-50 border border-slate-100 rounded-2xl p-4">
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500">{t('Descripción')}</label>
                        <input
                          value={line.description}
                          onChange={(event) => {
                            const value = event.target.value;
                            setFormState((prev) => ({
                              ...prev,
                              lines: prev.lines.map((l, i) => (i === index ? { ...l, description: value } : l))
                            }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                          placeholder={t('Material o servicio')}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500">{t('Cantidad')}</label>
                        <input
                          type="number"
                          min="1"
                          value={line.quantity}
                          onChange={(event) => {
                            const value = Number(event.target.value) || 1;
                            setFormState((prev) => ({
                              ...prev,
                              lines: prev.lines.map((l, i) => (i === index ? { ...l, quantity: value } : l))
                            }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500">{t('Unidad')}</label>
                        <input
                          value={line.unit}
                          onChange={(event) => {
                            const value = event.target.value;
                            setFormState((prev) => ({
                              ...prev,
                              lines: prev.lines.map((l, i) => (i === index ? { ...l, unit: value } : l))
                            }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                          required
                        />
                        {formState.lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setFormState((prev) => ({
                              ...prev,
                              lines: prev.lines.filter((_, i) => i !== index)
                            }))}
                            className="text-xs text-danger-600 hover:text-danger-700 font-semibold"
                          >{t('Eliminar')}</button>
                        )}
                      </div>
                      <div className="space-y-2 md:col-span-3">
                        <label className="text-xs font-semibold text-slate-500">{t('Precio unitario (estimado)')}</label>
                        <input
                          type="number"
                          min="0"
                          value={line.unitPrice}
                          onChange={(event) => {
                            const value = Number(event.target.value) || 0;
                            setFormState((prev) => ({
                              ...prev,
                              lines: prev.lines.map((l, i) => (i === index ? { ...l, unitPrice: value } : l))
                            }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-600">{t('Adjuntar archivos (PDF, imágenes, Excel)')}</label>
                <input
                  type="file"
                  accept=".pdf,image/*,.xlsx,.xls"
                  multiple
                  onChange={handleAttachmentUpload}
                  className="w-full px-4 py-3 border border-dashed border-emerald-200 rounded-xl text-sm text-slate-500 bg-emerald-50/40 hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                {uploadError && (
                  <p className="text-xs text-danger-600">{t(uploadError)}</p>
                )}
                {formState.attachments.length > 0 && (
                  <div className="space-y-2">
                    {formState.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs text-slate-600"
                      >
                        <div>
                          <p className="font-semibold text-slate-800">{t(attachment.name)}</p>
                          <p className="text-[11px] text-slate-400">
                            {formatFileSize(attachment.size)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(attachment.id)}
                          className="text-danger-600 font-semibold hover:text-danger-700"
                        >{t('Quitar')}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button variant="secondary" size="md" onClick={() => setShowCreateModal(false)}>{t('Cancelar')}</Button>
                <Button variant="primary" size="md" type="submit">{t('Guardar borrador')}</Button>
              </div>
            </form>
          </div>
        )}

      {selectedRequisition && (
          <div className="fixed inset-0 bg-slate-900/50 flex justify-end z-40">
            <div className="w-full max-w-xl h-full bg-white border-l border-slate-100 p-8 overflow-y-auto space-y-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs text-slate-400 uppercase">{t('Detalle requisición')}</p>
                  <h3 className="text-xl font-bold text-slate-900">{t(selectedRequisition.id)}</h3>
                  <p className="text-xs text-slate-500">{t('Solicitada por')} {t(findUserById(selectedRequisition.requesterId)?.name) || t(selectedRequisition.requesterName)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" icon={FileDown} onClick={handleExportRequisition}>{t('Exportar PDF')}</Button>
                  <button
                    className="text-sm text-slate-500 hover:text-slate-900"
                    onClick={() => setSelectedRequisition(null)}
                  >{t('cerrar')}</button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Estado')}</p>
                  <div className="mt-2">
                    <Badge tone={getStatusTone(selectedRequisition.status)}>
                      {t(getStatusText(selectedRequisition.status))}
                    </Badge>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Fecha requerida')}</p>
                  <p className="text-sm font-semibold text-slate-800">{formatDate(selectedRequisition.neededDate)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Parque / Área')}</p>
                  <p className="text-sm font-semibold text-slate-800">{t(selectedPark?.name) || '-'}</p>
                  <p className="text-xs text-slate-400">{t(selectedArea?.name) || '-'}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Centro de costo')}</p>
                  <p className="text-sm font-semibold text-slate-800">{t(selectedCostCenter?.code) || '-'}</p>
                  <p className="text-xs text-slate-400">{t(selectedCostCenter?.name) || ''}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 col-span-2">
                  <p className="text-xs text-slate-400 uppercase">{t('Paquete / WBS')}</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {selectedPackage
                      ? `${selectedPackage.code ? `${selectedPackage.code} · ` : ''}${selectedPackage.name}`
                      : '-'}
                  </p>
                </div>
              </div>

              <div className="bg-white border border-slate-100 rounded-xl">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">{t('Ítems solicitados')}</p>
                  <span className="text-xs text-slate-400">{t(selectedRequisition.lines.length)} {t('registros')}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {selectedRequisition.lines.map((line, index) => (
                    <div key={`selected-line-${index}`} className="px-4 py-3 text-sm text-slate-600">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-800">{t(line.description)}</p>
                          {line.sku && <p className="text-xs text-slate-400">{t('SKU')} {t(line.sku)}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-800">{t(line.quantity)} {t(line.unit)}</p>
                          {line.unitPrice && <p className="text-xs text-slate-400">{formatCurrency(line.unitPrice)}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedRequisition.attachments && selectedRequisition.attachments.length > 0 && (
                <div className="bg-white border border-slate-100 rounded-xl">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700">{t('Documentos adjuntos')}</p>
                    <span className="text-xs text-slate-400">
                      {t(selectedRequisition.attachments.length)} {t('archivo(s)')}</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {selectedRequisition.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="px-4 py-3 flex items-center justify-between text-sm text-slate-600"
                      >
                        <div>
                          <p className="font-semibold text-slate-800">{t(attachment.name)}</p>
                          <p className="text-xs text-slate-400">
                            {formatFileSize(attachment.size)} ·{' '}
                            {attachment.uploadedAt ? formatDate(attachment.uploadedAt) : t('Sin fecha')}
                          </p>
                        </div>
                        {attachment.dataUrl ? (
                          <a
                            href={attachment.dataUrl}
                            download={attachment.name}
                            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                          >{t('Descargar')}</a>
                        ) : (
                          <span className="text-xs text-slate-400">{t('Adjunto demo')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRequisition.justification && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase mb-2">{t('Justificación')}</p>
                  <p className="text-sm text-slate-600">{t(selectedRequisition.justification)}</p>
                </div>
              )}

              <div className="bg-white border border-slate-100 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase mb-2">{t('Acciones')}</p>
                {submitError && (
                  <div className="mb-3 rounded-xl border border-danger-100 bg-danger-50 px-4 py-3">
                    <p className="text-xs font-semibold text-danger-700 uppercase">{t('No se pudo avanzar')}</p>
                    <p className="text-xs text-danger-700 mt-1">{t(submitError)}</p>
                  </div>
                )}
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={t('Comentario / Observación')}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10"
                  rows={3}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {isRequester && selectedRequisition.status === 'borrador' && (
                    <Button variant="primary" icon={Send} onClick={() => handleSubmit(selectedRequisition.id)}>{t('Enviar a aprobación')}</Button>
                  )}
                  {(isSuperUser ||
                    selectedRequisition.approvals?.areaManager?.userId === currentUser?.id) &&
                    selectedRequisition.status === 'pendiente_area' && (
                    <>
                      <Button variant="primary" icon={CheckCircle2} onClick={() => handleApproval(selectedRequisition, 'area', true)}>{t('Aprobar área')}</Button>
                      <Button variant="danger" icon={XCircle} onClick={() => handleApproval(selectedRequisition, 'area', false)}>{t('Rechazar')}</Button>
                    </>
                  )}
                  {(isSuperUser ||
                    selectedRequisition.approvals?.purchaseController?.userId === currentUser?.id) &&
                    selectedRequisition.status === 'pendiente_controller' && (
                    <>
                      {selectedRequisition.budgetControl?.hasOverrun && (
                        <div className="w-full bg-warning-50 border border-warning-100 rounded-xl p-4 space-y-2">
                          <p className="text-xs font-semibold text-warning-700 uppercase">{t('Sobrepresupuesto detectado (PR)')}</p>
                          <p className="text-xs text-warning-700">{t('Partida:')} {t(selectedRequisition.budgetControl?.overrunDetail?.allocationName) || '—'} {t('· Exceso:')} {formatCurrency(selectedRequisition.budgetControl?.overrunDetail?.overrunAmount || 0)}
                          </p>
                          <label className="flex items-center gap-2 text-xs text-warning-700">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={Boolean(selectedRequisition.budgetControl?.controllerAcceptedOverrun)}
                              onChange={(event) => {
                                const accepted = event.target.checked;
                                const note = accepted
                                  ? window.prompt('Comentario opcional de aceptación de sobrepresupuesto:') || ''
                                  : '';
                                setRequisitionBudgetOverrunAcceptance(selectedRequisition.id, accepted, note);
                              }}
                            />{t('Controller acepta sobrepresupuesto (requerido para aprobar)')}</label>
                        </div>
                      )}
                      <Button variant="primary" icon={CheckCircle2} onClick={() => handleApproval(selectedRequisition, 'controller', true)}>{t('Aprobar controller')}</Button>
                      <Button variant="danger" icon={XCircle} onClick={() => handleApproval(selectedRequisition, 'controller', false)}>{t('Rechazar')}</Button>
                    </>
                  )}
                  {(['purchase_controller', 'super_user'].includes(currentUser?.role || '')) &&
                    selectedRequisition.status === 'pendiente_compra' && (
                    <div className="w-full space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-2">
                        <select
                          className="px-3 py-2 border border-slate-200 rounded-xl text-xs"
                          value={assignedBuyerId}
                          onChange={(event) => setAssignedBuyerId(event.target.value)}
                        >
                          <option value="">{t('Selecciona comprador...')}</option>
                          {users
                            .filter((user) => user.role === 'comprador')
                            .map((buyer) => (
                              <option key={buyer.id} value={buyer.id}>
                                {t(buyer.name)}
                              </option>
                            ))}
                        </select>
                        <input
                          className="px-3 py-2 border border-slate-200 rounded-xl text-xs"
                          placeholder={t('Código interno parque (ej: PVLEYDA02)')}
                          value={routingCodeInput}
                          onChange={(event) => setRoutingCodeInput(event.target.value.toUpperCase())}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleAssign}
                          disabled={!assignedBuyerId}
                          disabledReason="Selecciona un comprador antes de derivar"
                        >{t('Derivar a Comprador')}</Button>
                        {selectedRequisition.routedToBuyerId && (
                          <span className="text-xs text-slate-500">{t('Asignada a')} {t(findUserById(selectedRequisition.routedToBuyerId)?.name)}
                            {selectedRequisition.routingCode ? t(' · Código {0}', selectedRequisition.routingCode) : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {isBuyer && selectedRequisition.status === 'pendiente_compra' && (
                    <>
                      <select
                        value={convertSupplierId}
                        onChange={(event) => setConvertSupplierId(event.target.value)}
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      >
                        <option value="">{t('Proveedor')}</option>
                        {(suppliers || []).map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {t(supplier.name)}
                            {supplier.status ? ` · ${supplier.status}` : ''}
                          </option>
                        ))}
                      </select>
                      <select
                        value={convertCategoryId}
                        onChange={(event) => setConvertCategoryId(event.target.value)}
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      >
                        <option value="">{t('Categoría')}</option>
                        {(categories || []).map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {t(cat.name)}
                          </option>
                        ))}
                      </select>
                      <Button variant="primary" icon={ClipboardList} onClick={() => handleConvert(selectedRequisition)}>{t('Generar OC')}</Button>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase mb-2">{t('Historial')}</p>
                <div className="space-y-3">
                  {(selectedRequisition.history || []).map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between text-sm text-slate-600">
                      <div>
                        <p className="font-semibold text-slate-800">{t(entry.action)}</p>
                        {entry.note && <p className="text-xs text-slate-400">{t(entry.note)}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">{t(entry.timestamp)}</p>
                        <p className="text-xs text-slate-400">{t(findUserById(entry.userId)?.name) || t('Sistema')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PurchaseRequisitions;
